import { useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import useIntersection from '@hooks/useIntersection';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import pushRoute from '@utils/pushRoute';
import useCardsWithListingLock from '../testTab/useCardsWithListingLock';
import TestSeriesListingTestCard from './TestSeriesListingTestCard';
import TestSeriesListingTestCardSkeleton, {
  TEST_SERIES_LISTING_SKELETON_COUNT,
  TEST_SERIES_LISTING_SKELETON_COUNT_MOBILE
} from './TestSeriesListingTestCardSkeleton';
import TestSeriesListingEmptyState from './TestSeriesListingEmptyState';

const CONTENT_MIN_HEIGHT_DESKTOP = 434;
const CONTENT_MIN_HEIGHT_MOBILE = 434;

const GridWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  grid-column: span 18;
  width: 851px;
  margin-bottom: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-column: span 10;
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  min-height: ${({ $minHeight }) =>
    $minHeight ? `${$minHeight}px` : `${CONTENT_MIN_HEIGHT_DESKTOP}px`};
  transition: opacity 0.15s ease;

  ${({ $isRefreshing }) =>
    $isRefreshing
      ? `
    opacity: 0.55;
    pointer-events: none;
  `
      : ''}

  ${mediaQuery.uptoMobile} {
    min-height: ${({ $minHeight }) =>
      $minHeight ? `${$minHeight}px` : `${CONTENT_MIN_HEIGHT_MOBILE}px`};
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 273px);
  align-items: start;
  gap: ${Spacings.SPACING_4B};
  width: 852px;
  max-width: 100%;

  ${mediaQuery.uptoMobile} {
    grid-template-columns: 1fr;
    width: 100%;
  }
`;

const StatusMessage = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-secondary);
  text-align: center;

  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
  }
`;

const ScrollSentinel = styled.div`
  width: 100%;
  height: ${Spacings.SPACING_3B};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 48px;
`;

const getGoalUidFromQuery = (goalUID) =>
  Array.isArray(goalUID) ? goalUID[0] : goalUID;

const TestSeriesListingTestCardsGrid = ({
  cards,
  isInitialLoading,
  isRefreshing,
  isError,
  hasMore,
  onLoadMore,
  isValidating,
  onCardClick
}) => {
  const { query } = useRouter();
  const goalUid = getGoalUidFromQuery(query.goalUID);
  const cardsWithLock = useCardsWithListingLock(goalUid, cards);
  const isMobile = useIsMobile();
  const contentRef = useRef(null);
  const stableHeightRef = useRef(null);
  const skeletonCount = isMobile
    ? TEST_SERIES_LISTING_SKELETON_COUNT_MOBILE
    : TEST_SERIES_LISTING_SKELETON_COUNT;
  const { intersecting, ref: scrollSentinelRef } = useIntersection();
  const shouldLoadMore =
    !isInitialLoading &&
    !isError &&
    cardsWithLock.length > 0 &&
    hasMore &&
    !isValidating;

  useEffect(() => {
    if (shouldLoadMore && intersecting && onLoadMore) {
      onLoadMore();
    }
  }, [intersecting, shouldLoadMore, onLoadMore]);

  useLayoutEffect(() => {
    const element = contentRef.current;

    if (!element || isInitialLoading || isRefreshing) {
      return;
    }

    stableHeightRef.current = element.getBoundingClientRect().height;
  }, [cards.length, isError, isInitialLoading, isRefreshing]);

  const lockedMinHeight =
    isRefreshing && stableHeightRef.current ? stableHeightRef.current : null;

  const renderContent = () => {
    if (isInitialLoading) {
      return (
        <CardsGrid aria-busy="true">
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <TestSeriesListingTestCardSkeleton
              key={`test-card-skeleton-${index}`}
            />
          ))}
        </CardsGrid>
      );
    }

    if (isError) {
      return (
        <StatusMessage>
          We could not load tests right now. Please try again.
        </StatusMessage>
      );
    }

    if (!cardsWithLock.length) {
      return <TestSeriesListingEmptyState />;
    }

    return (
      <CardsGrid>
        {cardsWithLock.map(({ id, href, isLive = false, variant, ...test }) => (
          <TestSeriesListingTestCard
            key={id}
            variant={variant}
            isLive={isLive}
            href={href}
            onClick={
              href
                ? () =>
                    onCardClick
                      ? onCardClick({ id, href, isLive, variant, ...test })
                      : pushRoute(href)
                : undefined
            }
            categoryLabel={test.categoryLabel}
            title={test.title}
            description={test.description}
            subscriptionType={test.subscriptionType}
            showLock={test.showLock}
            questionCount={test.questionCount}
            durationMinutes={test.durationMinutes}
            testCount={test.testCount}
            dateRangeLabel={test.dateRangeLabel}
          />
        ))}
      </CardsGrid>
    );
  };

  return (
    <GridWrapper aria-label="Test series list" aria-busy={isInitialLoading}>
      <ContentArea
        ref={contentRef}
        $minHeight={lockedMinHeight}
        $isRefreshing={isRefreshing}
      >
        {renderContent()}
      </ContentArea>
      {hasMore && cardsWithLock.length > 0 ? (
        <ScrollSentinel ref={scrollSentinelRef} aria-hidden />
      ) : null}
      {isValidating && cardsWithLock.length > 0 && hasMore ? (
        <LoaderWrapper aria-busy="true" aria-label="Loading more tests">
          <CircularLoader />
        </LoaderWrapper>
      ) : null}
    </GridWrapper>
  );
};

export default TestSeriesListingTestCardsGrid;

TestSeriesListingTestCardsGrid.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      variant: PropTypes.oneOf(['quiz', 'test_series']),
      categoryLabel: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      subscriptionType: PropTypes.string.isRequired,
      showLock: PropTypes.bool,
      questionCount: PropTypes.number,
      durationMinutes: PropTypes.number,
      testCount: PropTypes.number,
      dateRangeLabel: PropTypes.string,
      isLive: PropTypes.bool,
      href: PropTypes.string
    })
  ),
  isInitialLoading: PropTypes.bool,
  isRefreshing: PropTypes.bool,
  isError: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  isValidating: PropTypes.bool,
  onCardClick: PropTypes.func
};

TestSeriesListingTestCardsGrid.defaultProps = {
  cards: [],
  isInitialLoading: false,
  isRefreshing: false,
  isError: false,
  hasMore: false,
  onLoadMore: undefined,
  isValidating: false,
  onCardClick: undefined
};
